<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">Proveedores</h1>
    </div>

    <div class="table-container">
      <div class="table-header">
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-start position-relative">
          <div class="w-full w-md-auto md-flex-1">
            <div class="d-flex justify-content-between align-items-center justify-content-lg-start">
              <button
                type="button"
                class="filter d-flex align-items-center justify-content-between border-0"
                @click="showFilters = !showFilters"
              >
                <i class="ri-filter-3-line text-dark font-weight-bold"></i>
                <div>Filtrar</div>
              </button>

              <div class="d-md-none">
                <button
                  type="button"
                  class="button-tooltip text-dark font-weight-bold m-0"
                  v-b-tooltip.hover
                  title="Recargar"
                  @click="loadData()"
                >
                  <i class="ri-loop-right-line"></i>
                </button>
              </div>
            </div>
          </div>

          <section class="w-full w-md-auto md-flex-1 d-flex justify-content-end align-items-center mt-2 mt-md-0">
            <div class="d-none d-md-flex">
              <button
                type="button"
                class="button-tooltip text-dark font-weight-bold"
                v-b-tooltip.hover
                title="Recargar"
                @click="loadData()"
              >
                <i class="ri-loop-right-line"></i>
              </button>
            </div>

            <div class="w-full w-md-auto d-flex flex-column flex-md-row">
              <SearchBar
                placeholder="Buscar por nombre, DNI o teléfono"
                @search="onSearch"
                @input="search = $event"
                class="flex-1"
              />

              <div class="text-right mt-2 mt-md-0">
                <b-button
                  :to="{ name: 'providers-create' }"
                  variant="none"
                  class="button button-primary ml-md-2"
                >Agregar</b-button>
              </div>
            </div>
          </section>

          <filter-menu
            :show="showFilters"
            @hide="showFilters = false"
            :options="filterOptions"
            :selectedOptionFilter="filters"
            @setFilters="setFilters"
          ></filter-menu>
        </div>

        <div class="w-100 d-flex flex-wrap align-items-center mt-2" v-if="showFiltersList">
          <span v-for="(val, name) in filterList" v-bind:key="name" class="filter-item">
            {{ getFilterName(name, val) }}
            <button
              type="button"
              class="text-white p-0 bg-transparent border-0 ml-2"
              @click="deleteFilter(name)"
            >
              <i class="ri-close-circle-line"></i>
            </button>
          </span>
        </div>
      </div>

      <div class="table-responsive mb-0 table-min-height">
        <table class="custom-table w-100">
          <thead>
            <tr>
              <th>Nombre</th>
              <th class="text-center">Tipo</th>
              <th class="text-center">Ciudad</th>
              <th class="text-center">Teléfono</th>
              <th class="text-center">Conductores</th>
              <th class="text-center">Vehículos</th>
              <th class="text-center">Estado</th>
              <th class="pr-md-4">Opciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="o in list" :key="o.id" class="clickable-row" @click="openDetail(o.id)">
              <td>
                <div>{{ o.business_name || o.contact_name }}</div>
                <div v-if="o.business_name" class="text-muted small">{{ o.contact_name }}</div>
              </td>
              <td class="text-center">
                <span :class="`badge ${getProviderType(o.provider_type).color}`">
                  {{ getProviderType(o.provider_type).label }}
                </span>
              </td>
              <td class="text-center">{{ o.city_name || "-" }}</td>
              <td class="text-center">{{ o.phone || "-" }}</td>
              <td class="text-center">{{ o.driver_count }}</td>
              <td class="text-center">{{ o.vehicle_count }}</td>
              <td class="text-center">
                <span :class="`badge ${getStatus(o.status).color}`">{{ getStatus(o.status).label }}</span>
              </td>
              <td class="text-center d_option pr-md-3" @click.stop>
                <DropdownTable>
                  <template slot="dropdown-content">
                    <DropdownItem text="Ver detalle" @click="openDetail(o.id)" />
                    <DropdownItem :text="getStatus(o.status).action" @click="openChangeStatus(o)" />
                  </template>
                </DropdownTable>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="table-footer d-flex justify-content-end align-items-center">
        <pagination
          :total="total"
          :page="page"
          :page-size="pageSize"
          :pages="pages"
          :changePage="changePage"
          :changePageSize="changePageSize"
          v-if="list.length"
        />
        <div class="table-no-results" v-else>
          <p class="m-0 px-3">No se encontraron resultados.</p>
        </div>
      </div>
    </div>

    <ConfirmationModal
      :show="confirmationModal.show"
      @hide="closeConfirmationModal"
      :data="confirmationModal"
      @confirm="confirmModal"
    />
  </div>
</template>

<script>
import { ProviderService } from "@/core/services";
import { Constants as LocalConstants } from "@/core/utils";

import {
  Alert,
  Constants,
  Util,
  PaginationMixin,
  DropdownTable,
  DropdownItem,
  SearchBar,
  FilterMenu,
  FilterMixin,
  ConfirmationModal,
  ConfirmationModalMixin,
} from "wize-admin";

export default {
  name: "ProviderList",
  components: {
    SearchBar,
    FilterMenu,
    DropdownTable,
    DropdownItem,
    ConfirmationModal,
  },
  mixins: [PaginationMixin, FilterMixin, ConfirmationModalMixin],
  data() {
    return {
      list: [],
      filterOptions: [
        {
          label: "Tipo",
          key: "provider_type",
          type: Constants.TypeFilters.SINGLE,
          options: [
            LocalConstants.ProviderType.ASOCIADO,
            LocalConstants.ProviderType.TRANSPORTISTA,
          ],
        },
        {
          label: "Estado",
          key: "status",
          type: Constants.TypeFilters.SINGLE,
          options: [
            LocalConstants.ProviderStatus.ACTIVE,
            LocalConstants.ProviderStatus.INACTIVE,
          ],
        },
      ],
    };
  },
  methods: {
    openDetail(id) {
      this.$router.push({ name: "providers-detail", params: { id } });
    },
    getProviderType(key) {
      return Util.searchValue(key, LocalConstants.ProviderType);
    },
    getStatus(key) {
      return Util.searchValue(parseInt(key), LocalConstants.ProviderStatus);
    },
    openChangeStatus(o) {
      this.selectedOption = o;
      this.openConfirmationModal({
        title: "Cambiar estado",
        description: "¿Está seguro que desea cambiar el estado?",
        acceptText: "Aceptar",
        action: "update",
      });
    },
    confirmModal() {
      this.closeConfirmationModal();
      if (this.confirmationModal.action === "update") this.changeStatus();
    },
    async changeStatus() {
      try {
        await this.$store.dispatch("app/loading", true);
        const status =
          this.selectedOption.status === LocalConstants.ProviderStatus.ACTIVE.key
            ? LocalConstants.ProviderStatus.INACTIVE.key
            : LocalConstants.ProviderStatus.ACTIVE.key;

        await ProviderService.changeStatus(this.selectedOption.id, status);
        await this.loadData();
        Alert.success("Estado actualizado correctamente");
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : "No se pudo cambiar el estado");
      } finally {
        await this.$store.dispatch("app/loading", false);
      }
    },
    async loadData() {
      try {
        await this.$store.dispatch("app/loading", true);

        const params = { page: this.page - 1, size: this.pageSize };
        const data = { search: this.search, ...this.filters };

        const response = await ProviderService.list(data, params);
        this.list = response.payload.items || [];
        this.total = response.payload?.metadata.totalElements;
        this.pages = response.payload?.metadata.totalPages;
      } catch (error) {
        console.error(error);
        Alert.error(error.errors ? error.errors.message : "No se pudo cargar la lista");
      } finally {
        await this.$store.dispatch("app/loading", false);
      }
    },
  },
  async mounted() {
    await this.loadData();
  },
};
</script>

<style lang="stylus" scoped>
.clickable-row
  cursor pointer
</style>
